import { PartyType } from "@/modules/transaction/common/enum";
import {
  getMessageLineValidationSchema,
  getReportCodeValidationSchema,
} from "@/modules/transaction/validations/validationSchema";
import {
  CARD_TRANSFER_MAX_AMOUNT,
  CARD_TRANSFER_MIN_AMOUNT,
  ONLY_DIGITS,
  TEXT_FIELD_VALIDATION_REGEX
} from "@/shared/common/constants";
import { ErrorMessages } from "@/shared/common/enums";
import type {
  ValidationSchemaAccountTransferProps,
  ValidationSchemaBudgetTransferProps,
  ValidationSchemaProps,
} from "@/shared/common/types";
import { cleanupAmount } from "@/shared/helpers";
import {
  getAccountNumberValidationSchema,
  getBeneficiaryNameValidationSchema,
  getBooleanValidationSchema,
  getCardNumberValidationSchema,
  getRequiredStringValidationSchema,
  getTemplateAmountValidationSchema,
  getTemplatePurposeValidationSchema,
  getTinValidationSchema,
} from "@/shared/validations";
import * as Yup from "yup";

export const createTemplateNameValidationSchema = ({ t }: ValidationSchemaProps) => {
  return Yup.object().shape({
    templateName: Yup.string()
      .trim()
      .max(
        50,
        t(ErrorMessages.MaxInput, { count: 50 })
      ).required(t(ErrorMessages.RequiredField)),
  });
};

export const accountTransferTemplateValidationSchema = ({
  t, isAccountNumberInvalid
}: ValidationSchemaAccountTransferProps) => {
  return Yup.object().shape({
    creditAccount: getAccountNumberValidationSchema({ t, isAccountNumberInvalid }),
    debitAccount: Yup.string().nullable(),
    beneficiary: getBeneficiaryNameValidationSchema({ t }),
    amount: getTemplateAmountValidationSchema({ t }),
    purpose: getTemplatePurposeValidationSchema({ t }),
    beneficiaryLegalStatus: Yup.string().nullable(),
    commissionAccount: Yup.string().nullable(),
  });
};

interface CardTransferTemplateValidationProps extends ValidationSchemaProps {
  isCardNumberInvalid: boolean;
}

export const cardTransferTemplateValidationSchema = (
  { t, isCardNumberInvalid }: CardTransferTemplateValidationProps) =>
  Yup.object().shape({
    commissionAccount: Yup.string().nullable(),
    creditCardNumber: getCardNumberValidationSchema({ t })
      .test(
        "card-holder-valid",
        t(ErrorMessages.InvalidCardNumber),
        (value) => !value || value.length < 16 || !isCardNumberInvalid
      ),
    debitAccount: getRequiredStringValidationSchema({ t }),
    amount: Yup.string()
      .transform(value => cleanupAmount(value, ""))
      .test(
        "min",
        t("errorMessages.amountMustBeGreater", { amount: CARD_TRANSFER_MIN_AMOUNT }),
        (value) => !value || Number(value) >= CARD_TRANSFER_MIN_AMOUNT
      )
      .test(
        "max",
        t("errorMessages.amountMustBeLess", { amount: CARD_TRANSFER_MAX_AMOUNT }),
        (value) => !value || Number(value) <= CARD_TRANSFER_MAX_AMOUNT
      )
      .nullable(),
    purpose: getTemplatePurposeValidationSchema({ t }),
  });

export const ownAccountsTransferTemplateValidationSchema = ({ t }: ValidationSchemaProps) => {
  return Yup.object().shape({
    creditAccount: getRequiredStringValidationSchema({ t }),
    debitAccount: getRequiredStringValidationSchema({ t }),
    amount: getTemplateAmountValidationSchema({ t }),
    purpose: getTemplatePurposeValidationSchema({ t }),
  });
};

export const currencyExchangeTemplateValidationSchema = ({ t }: ValidationSchemaProps) => {
  return Yup.object().shape({
    creditAccount: getRequiredStringValidationSchema({ t }),
    debitAccount: getRequiredStringValidationSchema({ t }),
    amount: getTemplateAmountValidationSchema({ t }),
    wantAmount: getTemplateAmountValidationSchema({ t }),
    purpose: getTemplatePurposeValidationSchema({ t }),
  });
};

export const loanRepaymentTemplateValidationSchema = (
  { t }: ValidationSchemaProps) =>
  Yup.object().shape({
    loanContractNumber: Yup.string().required(t(ErrorMessages.RequiredField)),
    loanRepaymentType: Yup.string().required(t(ErrorMessages.RequiredField)),
    debitAccount: getRequiredStringValidationSchema({ t }),
    amount: getTemplateAmountValidationSchema({ t }),
  });

export const budgetTransferTemplateValidationSchema = (
  { t, isTinValid }: ValidationSchemaBudgetTransferProps) =>
  Yup.object().shape({
    creditBudgetAccount: getAccountNumberValidationSchema({ t }),
    amount: getTemplateAmountValidationSchema({ t }),
    tin: getTinValidationSchema({ t }),
    purpose: getTemplatePurposeValidationSchema({ t }),
    isAuthorizedTransfer: getBooleanValidationSchema(),
    regionCode: Yup.string().nullable(),
    authorizedBudgetTransferParty: Yup.object().shape({})
      .when("isAuthorizedTransfer", ([isAuthorizedTransfer], schema) => {
        if (!isAuthorizedTransfer) {
          return schema.strip();
        }
        return schema.shape({
          fullName: Yup.string()
            .nullable()
            .matches(
              TEXT_FIELD_VALIDATION_REGEX,
              { message: t(ErrorMessages.InvalidPurpose), excludeEmptyString: true }
            )
            .max(140, t("errorMessages.maxFullName")),
          residency: Yup.string().nullable(),
          tin: Yup.string()
            .nullable()
            .test("tin-valid", t(ErrorMessages.InvalidTin), () => isTinValid)
            .when(
              "partyType",
              ([partyType], schema) => {
                return partyType !== PartyType.NaturalPerson
                  ? schema
                    .nullable()
                    .matches(
                      ONLY_DIGITS,
                      { message: t("errorMessages.onlyDigits"), excludeEmptyString: true }
                    )
                    .test(
                      "tin-length",
                      t("errorMessages.tinInvalidLength", { length: 8 }),
                      (value) => !value || value.length === 8
                    )
                  : schema.strip();
              }
            ),
          partyType: Yup.string().nullable(),
          ssn: Yup.string()
            .nullable()
            .when(
              "partyType",
              ([partyType], schema) => {
                return partyType === PartyType.NaturalPerson ?
                  schema
                    .matches(
                      ONLY_DIGITS,
                      { message: t("errorMessages.onlyDigits"), excludeEmptyString: true }
                    )
                    .test(
                      "ssn-length",
                      t(ErrorMessages.SsnInvalidLength, { length: 10 }),
                      (value) => !value || value.length === 10
                    )
                  : schema.strip();
              }
            ),
          documentType: Yup.string().nullable(),
          documentNumber: Yup.string()
            .matches(
              TEXT_FIELD_VALIDATION_REGEX,
              { message: t(ErrorMessages.InvalidDocumentNumber), excludeEmptyString: true }
            )
            .max(100, t("errorMessages.maxDocumentNumber", { count: 100 }))
            .nullable(),
          address: Yup.string()
            .nullable()
            .matches(
              TEXT_FIELD_VALIDATION_REGEX,
              { message: t(ErrorMessages.InvalidPurpose), excludeEmptyString: true }
            )
            .max(33, t("errorMessages.maxAddress", { count: 33 })),
          additionalInfo: Yup.string()
            .nullable()
            .matches(
              TEXT_FIELD_VALIDATION_REGEX,
              { message: t(ErrorMessages.InvalidPurpose), excludeEmptyString: true }
            )
            .max(33, t("errorMessages.maxAdditionalInfo", { count: 33 })),
          authorizedPerson: Yup.string()
            .nullable()
            .matches(
              TEXT_FIELD_VALIDATION_REGEX,
              { message: t(ErrorMessages.InvalidPurpose), excludeEmptyString: true }
            )
            .max(33, t("errorMessages.maxAuthorizedPerson", { count: 33 })),
        });
      }),
    messages: Yup.object()
      .when("regionCode", ([regionCode], schema) =>
        schema.shape({
          reportCode: getReportCodeValidationSchema({ t, regionCode }),
          message1: getMessageLineValidationSchema({ t }),
          message2: getMessageLineValidationSchema({ t }),
          message3: getMessageLineValidationSchema({ t }),
          message4: getMessageLineValidationSchema({ t }),
          message5: getMessageLineValidationSchema({ t }),
          message6: getMessageLineValidationSchema({ t }),
        })
      ),
  });
